/**
 * 用途：Gatekeeper SSO 单点登录回调处理页，接收授权码，换取 access_token 后写入 localStorage，
 *       再跳转回用户原始访问路径
 * 路由：/sso/callback
 */
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { redirectToSSO } from '../lib/sso'
import type { AdContext } from '../store/useAppStore'
import useAppStore from '../store/useAppStore'
const GATEKEEPER_URL = (import.meta.env.VITE_GATEKEEPER_URL || 'https://test-gatekeeper.xmslol.cn').replace(/\/+$/, '')
const OAUTH_CLIENT_ID = import.meta.env.VITE_OAUTH_CLIENT_ID || 'ad-platform'
const OAUTH_REDIRECT_URI = import.meta.env.VITE_OAUTH_REDIRECT_URI || `${window.location.origin}/sso/callback`

export default function SSOCallback() {
  const [searchParams] = useSearchParams()
  const handled = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const setAdContext = useAppStore((s) => s.setAdContext)

  useEffect(() => {
    if (handled.current) return
    handled.current = true

    void (async () => {
      const code = searchParams.get('code')
      const returnedState = searchParams.get('state')

      const savedState = localStorage.getItem('oauth_state')
      const codeVerifier = localStorage.getItem('oauth_code_verifier')
      const redirectAfter = localStorage.getItem('oauth_redirect_after') ?? '/'
      localStorage.removeItem('oauth_state')
      localStorage.removeItem('oauth_code_verifier')
      localStorage.removeItem('oauth_redirect_after')

      if (!code) {
        setError('授权码缺失，请重试')
        return
      }

      // sessionStorage 丢失（极少情况：多 tab、跨域跳转等），直接重发 SSO
      if (!codeVerifier) {
        await redirectToSSO(redirectAfter)
        return
      }

      if (savedState && returnedState !== savedState) {
        // state 不匹配说明有旧的 SSO 流程残留，重新发起一次即可
        await redirectToSSO(redirectAfter)
        return
      }

      try {
        const res = await fetch(`${GATEKEEPER_URL}/api/oauth/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            code_verifier: codeVerifier,
            client_id: OAUTH_CLIENT_ID,
            redirect_uri: OAUTH_REDIRECT_URI,
          }),
        })

        if (!res.ok) {
          const err = await res.json().catch(() => ({})) as { error?: string; error_description?: string }
          setError(err.error_description ?? err.error ?? '授权失败，请重试')
          return
        }

        const data = await res.json() as {
          access_token: string
          user: { id: string; name: string; email: string | null }
        }

        // Exchange gatekeeper token for a local JWT that the backend can validate
        const exchRes = await fetch('/api/v1/auth/sso/exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: data.access_token }),
        })

        if (!exchRes.ok) {
          const exchErr = await exchRes.json().catch(() => ({})) as { error?: string }
          setError(exchErr.error ?? '令牌交换失败，请重试')
          return
        }

        const exchData = await exchRes.json() as {
          token: string
          expires_at: string
          user: { id: string; name: string; email: string }
          ad_context: AdContext | null
        }

        localStorage.setItem('access_token', exchData.token)
        // 记忆期 90 天：期间 token 过期会自动走 SSO 无感续期，无需手动重登
        document.cookie = `session_remember=1; max-age=${90 * 24 * 3600}; path=/; SameSite=Strict`
        if (exchData.user) {
          localStorage.setItem('user_info', JSON.stringify(exchData.user))
        }
        // 存储后端解析好的广告权限上下文，供 securityFilter / 侧边栏过滤使用
        setAdContext(exchData.ad_context ?? null)

        window.location.replace(redirectAfter)
      } catch {
        setError('网络错误，请检查连接后重试')
      }
    })()
  }, [searchParams])

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0b0f19',
        color: '#fff',
        gap: 16,
        fontFamily: 'system-ui, sans-serif',
      }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
        </svg>
        <p style={{ fontSize: 14, color: '#ef4444' }}>{error}</p>
        <button
          onClick={() => { void redirectToSSO('/') }}
          style={{ marginTop: 8, padding: '8px 20px', borderRadius: 6, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 13 }}
        >
          重新登录
        </button>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0b0f19',
      color: '#fff',
      gap: 16,
      fontFamily: 'system-ui, sans-serif',
    }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.5">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      </svg>
      <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>正在完成授权登录…</p>
    </div>
  )
}
